import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Rocket, X, Home, Radio, MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const STORAGE_KEY = "pandax_tutorial_seen_v1";

/**
 * First-run onboarding modal. Renders once per user, ever — dismissal is
 * persisted both to localStorage AND to the server (`onboarding_seen` on
 * the user doc) so returning admins aren't nagged after cache clears or
 * device switches.
 */
export default function OnboardingModal() {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Server-side flag wins — if the user already dismissed it on any device
    // we don't even bother reading localStorage.
    if (user.onboarding_seen) return;
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
      const t = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(t);
    } catch { /* localStorage may be blocked (Safari private) — still show once */ }
  }, [user]);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* silent */ }
    setOpen(false);
    // Persist server-side so returning users on a different device / cache-cleared
    // browser also don't get re-nagged.
    api.post("/users/me/onboarding-seen")
      .then(() => setUser((u) => (u ? { ...u, onboarding_seen: true } : u)))
      .catch((e) => console.debug("[onboarding] server persist failed", e));
  };

  if (!open || !user) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={dismiss}
      data-testid="onboarding-modal"
    >
      <div
        className="relative w-full max-w-md bg-zinc-950 border border-white/10 rounded-3xl p-6 pt-8 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full bg-rose-600/20 blur-3xl"
        />
        <button
          onClick={dismiss}
          data-testid="onboarding-close"
          className="absolute top-3 right-3 text-zinc-500 hover:text-white"
        >
          <X size={18} />
        </button>
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-rose-500 to-yellow-400 flex items-center justify-center text-black mb-4 shadow-lg shadow-rose-500/30">
          <Rocket size={26} />
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-rose-400 mb-1">Welcome to My Panda X</div>
        <h2 className="font-display text-2xl text-white mb-2">
          Hey {user.display_name} — want the 60-second tour?
        </h2>
        <p className="text-sm text-zinc-400 leading-relaxed mb-5">
          We'll walk you through the Feed, going live, Social Meet, gifts, battles, and how to
          earn your first Battle Champion crown.
        </p>
        <div className="flex flex-wrap gap-2 mb-5">
          {[
            { Icon: Home, label: "Feed" },
            { Icon: Radio, label: "Live" },
            { Icon: MapPin, label: "Social Meet" },
          ].map(({ Icon, label }) => (
            <div key={label} className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-xs text-zinc-300">
              <Icon size={12} />
              {label}
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Link
            to="/tutorial"
            onClick={dismiss}
            data-testid="onboarding-start"
            className="flex-1 bg-rose-600 hover:bg-rose-500 text-white font-semibold py-3 rounded-full text-sm text-center"
          >
            Start the tour
          </Link>
          <button
            onClick={dismiss}
            data-testid="onboarding-skip"
            className="px-4 border border-white/10 hover:bg-white/5 text-zinc-400 rounded-full text-sm"
          >
            Skip
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
